import type { Omt, QuestionResponseSchema } from "@/hera-api/client/types.gen";
import type { CompanyContactAnswer } from "@/lib/service-transfer-deregistration";

export type ServiceTransferWizardStepId =
  | "transfer_date"
  | "registrant"
  | "registration_questions"
  | "omt"
  | "ams"
  | "enact_omt"
  | "pass_prior"
  | "confirm";

export type ServiceTransferPath = "A" | "B";

/** Detect [OMT] / [AMS] (or parentheses) tags in question text. */
export function tagFromQuestionText(text: string): "OMT" | "AMS" | null {
  const m =
    text.match(/\[\s*(OMT|AMS)\s*\]/i) ?? text.match(/\(\s*(OMT|AMS)\s*\)/i);
  if (m) return m[1].toUpperCase() as "OMT" | "AMS";
  const normalized = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (/^AMS$/i.test(normalized)) return "AMS";
  if (/^OMT$/i.test(normalized)) return "OMT";
  // Match backend _tag_from_question_text: untagged "Asset Manager…" → AMS
  if (/asset\s*manager/i.test(normalized)) return "AMS";
  return null;
}

const AMS_OMT_ANSWER_TYPES = new Set([
  "company_contact",
  "name_address_contact",
]);

/** Registration (non-deregistration) contact question tagged OMT or AMS. */
export function getRegistrationTaggedQuestion(
  questions: QuestionResponseSchema[] | undefined,
  tag: "OMT" | "AMS",
): QuestionResponseSchema | null {
  for (const q of questions ?? []) {
    if (q.question_type === "deregistration") continue;
    if (q.question_type && q.question_type !== "registration") continue;
    if (!AMS_OMT_ANSWER_TYPES.has(q.answer_type)) continue;
    if (tagFromQuestionText(q.question) === tag) return q;
  }
  return null;
}

/** Stable identity for OMT company-contact answers (mirrors backend). */
export function omtAnswerIdentity(
  raw: unknown,
): ["party" | "company" | "raw", string] | null {
  if (raw == null) return null;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    const partyId = obj.party_id;
    if (partyId != null && String(partyId).trim()) {
      return ["party", String(partyId).trim().toLowerCase()];
    }
    for (const key of ["companyName", "company_name", "name"] as const) {
      const val = obj[key];
      if (typeof val === "string" && val.trim()) {
        return ["company", val.trim().toLowerCase()];
      }
    }
    return null;
  }
  if (typeof raw === "string" && raw.trim()) {
    return ["raw", raw.trim().toLowerCase()];
  }
  return null;
}

export function omtPartyChanged(
  oldAnswer: unknown,
  newAnswer: unknown,
): boolean {
  const a = omtAnswerIdentity(oldAnswer);
  const b = omtAnswerIdentity(newAnswer);
  if (a == null && b == null) return false;
  if (a == null || b == null) return true;
  return a[0] !== b[0] || a[1] !== b[1];
}

export function shouldAskEnactOmtChange(opts: {
  omtChanged: boolean;
  jurisdictionOmt: Omt | null | undefined;
}): boolean {
  // OMT change under CHANGE is always Path A — no enact prompt.
  void opts;
  return false;
}

/** Path A when OMT party changed and jurisdiction ordinance is CHANGE. */
export function resolveTransferPath(opts: {
  omtChanged: boolean;
  jurisdictionOmt: Omt | null | undefined;
  enactOmtChange?: boolean | null;
}): ServiceTransferPath {
  void opts.enactOmtChange;
  if (opts.omtChanged && opts.jurisdictionOmt === "change") {
    return "A";
  }
  return "B";
}

/** Auto-pass prior unpaid when OMT changed under UPDATE_INCLUDING (no ask). */
export function autoPassPriorUnpaid(opts: {
  omtChanged: boolean;
  jurisdictionOmt: Omt | null | undefined;
}): boolean {
  return (
    opts.omtChanged &&
    opts.jurisdictionOmt === "update_including_outstanding_fees"
  );
}

export function shouldAskPassPriorUnpaid(opts: {
  path: ServiceTransferPath;
  hasPriorUnpaid: boolean;
  omtChanged: boolean;
  jurisdictionOmt: Omt | null | undefined;
}): boolean {
  if (opts.path === "A") return false;
  if (!opts.hasPriorUnpaid) return false;
  if (autoPassPriorUnpaid(opts)) return false;
  return true;
}

export function buildServiceTransferWizardSteps(opts: {
  hasOmtQuestion: boolean;
  hasAmsQuestion: boolean;
  /** Other registration questions beyond OMT/AMS (shown as one step). */
  hasOtherRegistrationQuestions?: boolean;
  omtChanged: boolean;
  jurisdictionOmt: Omt | null | undefined;
  hasPriorUnpaid: boolean;
  /** Kept for callers; enact is no longer asked (CHANGE + OMT change → Path A). */
  enactOmtChange?: boolean | null;
}): ServiceTransferWizardStepId[] {
  const steps: ServiceTransferWizardStepId[] = ["transfer_date", "registrant"];
  if (opts.hasOtherRegistrationQuestions) {
    steps.push("registration_questions");
  }
  if (opts.hasOmtQuestion) steps.push("omt");
  if (opts.hasAmsQuestion) steps.push("ams");

  const path = resolveTransferPath(opts);
  if (
    shouldAskPassPriorUnpaid({
      path,
      hasPriorUnpaid: opts.hasPriorUnpaid,
      omtChanged: opts.omtChanged,
      jurisdictionOmt: opts.jurisdictionOmt,
    })
  ) {
    steps.push("pass_prior");
  }

  steps.push("confirm");
  return steps;
}

/**
 * Best-effort proposed display id when preview API is unavailable.
 * Original `CODE-N` → `CODE-N-1`; already-suffixed `CODE-N-2` → `CODE-N-3`.
 */
export function proposeNextDisplayId(currentDisplayId: string): string {
  const trimmed = currentDisplayId.trim();
  if (!trimmed) return "";
  const m = trimmed.match(/^(.*)-(\d+)$/);
  if (m) {
    // Prefer treating a trailing segment as suffix when the base still looks like CODE-ROOT
    // (e.g. SFO-12345-2 → SFO-12345-3). Originals without suffix get -1.
    const base = m[1];
    const suffix = Number(m[2]);
    if (base.includes("-") && Number.isFinite(suffix)) {
      return `${base}-${suffix + 1}`;
    }
  }
  return `${trimmed}-1`;
}

export function hasPriorUnpaidPeriods(
  payments: ReadonlyArray<{
    paid_in_full: boolean;
    selected?: boolean | null;
    period_end_date: string | null;
  }>,
  transferDateIso: string,
): boolean {
  if (!transferDateIso) return false;
  return payments.some((p) => {
    if (p.paid_in_full) return false;
    if (p.selected === false) return false;
    if (!p.period_end_date) return false;
    return p.period_end_date < transferDateIso;
  });
}

/** Unpaid selected periods that do not end before the transfer date (current/future). */
export function hasCurrentOrLaterUnpaidPeriods(
  payments: ReadonlyArray<{
    paid_in_full: boolean;
    selected?: boolean | null;
    period_end_date: string | null;
  }>,
  transferDateIso: string,
): boolean {
  if (!transferDateIso) return false;
  return payments.some((p) => {
    if (p.paid_in_full) return false;
    if (p.selected === false) return false;
    if (!p.period_end_date) return false;
    return p.period_end_date >= transferDateIso;
  });
}

export const PAYMENT_RESPONSIBILITY_LABELS = {
  none: "No outstanding fees",
  currentOnly: "New registrant responsible for unpaid current period",
  split:
    "New registrant responsible for unpaid current period and previous registrant responsible for previous outstanding fees",
  allUnpaid: "New registrant responsible for all unpaid fees",
  omtChange: "New registrant responsible for new registration as an OMT change",
} as const;

export type PaymentResponsibilityLabel =
  (typeof PAYMENT_RESPONSIBILITY_LABELS)[keyof typeof PAYMENT_RESPONSIBILITY_LABELS];

/** Confirm-step copy for who owes what after the transfer. */
export function paymentResponsibilityLabel(opts: {
  path: ServiceTransferPath;
  hasPriorUnpaid: boolean;
  hasCurrentUnpaid: boolean;
  passPriorUnpaid: boolean;
}): PaymentResponsibilityLabel {
  if (opts.path === "A") {
    return PAYMENT_RESPONSIBILITY_LABELS.omtChange;
  }
  if (opts.hasPriorUnpaid && opts.passPriorUnpaid) {
    return PAYMENT_RESPONSIBILITY_LABELS.allUnpaid;
  }
  if (opts.hasPriorUnpaid) {
    return PAYMENT_RESPONSIBILITY_LABELS.split;
  }
  if (opts.hasCurrentUnpaid) {
    return PAYMENT_RESPONSIBILITY_LABELS.currentOnly;
  }
  return PAYMENT_RESPONSIBILITY_LABELS.none;
}

export function isCompanyContactAnswerPresent(
  answer: CompanyContactAnswer | null | undefined,
): boolean {
  if (!answer) return false;
  if (answer.party_id != null && String(answer.party_id).trim()) return true;
  for (const key of ["companyName", "company_name", "name"] as const) {
    const val = answer[key];
    if (typeof val === "string" && val.trim()) return true;
  }
  return false;
}

export function cloneCompanyContactAnswer(
  answer: unknown,
): CompanyContactAnswer | null {
  if (!answer || typeof answer !== "object" || Array.isArray(answer)) {
    return null;
  }
  return { ...(answer as CompanyContactAnswer) };
}

export function formatCompanyContactLabel(
  answer: CompanyContactAnswer | null | undefined,
): string {
  if (!answer) return "—";
  const company =
    (typeof answer.companyName === "string" && answer.companyName.trim()) ||
    (typeof answer.company_name === "string" && answer.company_name.trim()) ||
    "";
  const person =
    typeof answer.name === "string" && answer.name.trim()
      ? answer.name.trim()
      : "";
  return company || person || "—";
}

export const WIZARD_STEP_TITLES: Record<ServiceTransferWizardStepId, string> = {
  transfer_date: "Service transfer date",
  registrant: "New registrant",
  registration_questions: "Registration questions",
  omt: "New OMT",
  ams: "New AMS",
  enact_omt: "Enact OMT change",
  pass_prior: "Outstanding payment responsibility",
  confirm: "Confirm transfer",
};
